import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import CSS from '../styles/global.css';
// heroImage: import heroImage from '../assets/hero.jpg';
// For demo we use a placeholder — swap in your actual import above.
const heroImage = 'https://images.unsplash.com/photo-1519741497674-611481863552?w=900&q=80';

// ─── tiny hook: fade-in on scroll ─────────────────────────────────────────────
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

// ─── decorative SVG motif (abstracted paisley-ish) ────────────────────────────
const Motif = ({ className }) => (
  <svg className={className} viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="60" cy="60" r="56" stroke="currentColor" strokeWidth="1" strokeDasharray="4 6" />
    <circle cx="60" cy="60" r="36" stroke="currentColor" strokeWidth="1" opacity=".5" />
    <circle cx="60" cy="60" r="16" stroke="currentColor" strokeWidth="1.5" opacity=".8" />
    <circle cx="60" cy="60" r="4"  fill="currentColor" opacity=".6" />
    <line x1="60" y1="4"  x2="60" y2="116" stroke="currentColor" strokeWidth=".5" opacity=".3" />
    <line x1="4"  y1="60" x2="116" y2="60" stroke="currentColor" strokeWidth=".5" opacity=".3" />
  </svg>
);

// ─── stat pill ────────────────────────────────────────────────────────────────
function Stat({ number, label }) {
  return (
    <div className="hp-stat">
      <span className="hp-stat__number">{number}</span>
      <span className="hp-stat__label">{label}</span>
    </div>
  );
}

// ─── feature card ─────────────────────────────────────────────────────────────
function FeatureCard({ icon, title, body, href, linkLabel, delay }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`hp-card ${visible ? 'hp-card--visible' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <span className="hp-card__icon" aria-hidden="true">{icon}</span>
      <h3 className="hp-card__title">{title}</h3>
      <p className="hp-card__body">{body}</p>
      <Link className="hp-card__link" to={href}>
        {linkLabel} <span aria-hidden="true">→</span>
      </Link>
    </div>
  );
}

// ─── testimonial ──────────────────────────────────────────────────────────────
function Testimonial({ quote, name, detail, delay }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={`hp-testimonial ${visible ? 'hp-testimonial--visible' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <p className="hp-testimonial__quote">"{quote}"</p>
      <div className="hp-testimonial__author">
        <span className="hp-testimonial__name">{name}</span>
        <span className="hp-testimonial__detail">{detail}</span>
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────
const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const id = 'hp-styles';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = CSS;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  return (
    <div className="hp-root">

      {/* ── navbar ── */}
      <nav className={`hp-nav ${scrolled ? 'hp-nav--scrolled' : ''}`}>
        <Link to="/" className="hp-nav__brand">
          <span className="hp-nav__brand-icon">⬡</span>
          Look4Partner
        </Link>
        <div className={`hp-nav__links ${menuOpen ? 'hp-nav__links--open' : ''}`}>
          <Link to="/matches" className="hp-nav__link" onClick={() => setMenuOpen(false)}>Find Matches</Link>
          <Link to="/contact-us" className="hp-nav__link" onClick={() => setMenuOpen(false)}>Contact</Link>
          <Link to="/signin" className="hp-nav__btn hp-nav__btn--ghost" onClick={() => setMenuOpen(false)}>Log in</Link>
          <Link to="/signup" className="hp-nav__btn hp-nav__btn--solid" onClick={() => setMenuOpen(false)}>Sign up free</Link>
        </div>
        <button className="hp-nav__burger" onClick={() => setMenuOpen(m => !m)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
      </nav>

      {/* ── hero ── */}
      <section className="hp-hero">
        <Motif className="hp-hero__motif hp-hero__motif--tl" />
        <Motif className="hp-hero__motif hp-hero__motif--br" />

        <div className="hp-hero__inner">
          {/* left: copy */}
          <div className="hp-hero__copy">
            <span className="hp-hero__eyebrow">Trusted by thousands of families</span>
            <h1 className="hp-hero__headline">
              Where <em>Tradition</em><br />Meets Technology
            </h1>
            <p className="hp-hero__subline">
              A thoughtful space for South Asians seeking a life partner —
              guided by values, powered by intelligent matching.
            </p>
            <div className="hp-hero__actions">
              <Link to="/signup" className="hp-cta hp-cta--primary">
                Create your profile
              </Link>
              <Link to="/matches" className="hp-cta hp-cta--ghost">
                Browse matches
              </Link>
            </div>
            <div className="hp-hero__stats">
              <Stat number="50K+" label="Profiles" />
              <div className="hp-stat-divider" />
              <Stat number="3,200+" label="Marriages" />
              <div className="hp-stat-divider" />
              <Stat number="4.9★" label="Rated" />
            </div>
          </div>

          {/* right: image */}
          <div className="hp-hero__img-wrap">
            <div className="hp-hero__img-frame">
              <img src={heroImage} alt="Happy couple" className="hp-hero__img" />
            </div>
            <div className="hp-hero__img-badge">
              <span className="hp-hero__img-badge-icon">💍</span>
              <div>
                <strong>Priya & Arjun</strong>
                <span>Matched on Look4Partner</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── how it works ── */}
      <section className="hp-how">
        <div className="hp-section-inner">
          <span className="hp-section-eyebrow">Simple by design</span>
          <h2 className="hp-section-title">Three steps to your match</h2>
          <div className="hp-steps">
            {[
              { n: '01', title: 'Build your profile', body: 'Answer thoughtful questions about your values, background, and what you\'re looking for.' },
              { n: '02', title: 'Get curated matches', body: 'Our algorithm surfaces compatible profiles based on compatibility, not just demographics.' },
              { n: '03', title: 'Connect with intention', body: 'Message your matches in a respectful, family-friendly environment.' },
            ].map((s, i) => (
              <div key={s.n} className="hp-step">
                <span className="hp-step__number">{s.n}</span>
                <h3 className="hp-step__title">{s.title}</h3>
                <p className="hp-step__body">{s.body}</p>
                {i < 2 && <span className="hp-step__arrow" aria-hidden="true">→</span>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── features ── */}
      <section className="hp-features">
        <div className="hp-section-inner">
          <span className="hp-section-eyebrow">Everything you need</span>
          <h2 className="hp-section-title">Built for meaningful connections</h2>
          <div className="hp-cards">
            <FeatureCard
              icon="👤" title="Create Your Profile"
              body="A rich, multi-step profile that captures who you really are — beyond age and location."
              href="/signup" linkLabel="Get started"
              delay={0}
            />
            <FeatureCard
              icon="🔍" title="Find Matches"
              body="Browse curated profiles filtered by values, family background, lifestyle, and more."
              href="/matches" linkLabel="Explore matches"
              delay={100}
            />
            <FeatureCard
              icon="🤝" title="Family-Friendly"
              body="Designed with South Asian family values in mind — discreet, respectful, and trustworthy."
              href="/signup" linkLabel="Learn more"
              delay={200}
            />
            <FeatureCard
              icon="💡" title="Smart Compatibility"
              body="Our matching goes beyond surface traits — it aligns on conflict resolution, finances, and long-term goals."
              href="/signup" linkLabel="See how"
              delay={300}
            />
          </div>
        </div>
      </section>

      {/* ── testimonials ── */}
      <section className="hp-testimonials">
        <Motif className="hp-testimonials__motif" />
        <div className="hp-section-inner">
          <span className="hp-section-eyebrow">Real stories</span>
          <h2 className="hp-section-title">Families brought together</h2>
          <div className="hp-testimonials__grid">
            <Testimonial
              quote="We were matched based on shared values around family and faith. It felt different from other apps — more intentional."
              name="Meera & Vikram" detail="Married 2024 · Mumbai & Toronto"
              delay={0}
            />
            <Testimonial
              quote="My parents were skeptical of online matching, but Look4Partner's approach felt familiar and trustworthy to them too."
              name="Sana & Imran" detail="Engaged 2024 · London"
              delay={120}
            />
            <Testimonial
              quote="The detail in the profile questions helped us understand each other before our first call. It saved so much time."
              name="Kavitha & Rohan" detail="Married 2023 · Bangalore"
              delay={240}
            />
          </div>
        </div>
      </section>

      {/* ── CTA banner ── */}
      <section className="hp-banner">
        <Motif className="hp-banner__motif" />
        <div className="hp-section-inner hp-banner__inner">
          <h2 className="hp-banner__title">Your story starts here.</h2>
          <p className="hp-banner__sub">Join thousands of families who trusted Look4Partner to find the right match.</p>
          <Link to="/signup" className="hp-cta hp-cta--banner">
            Create a free profile
          </Link>
        </div>
      </section>

      {/* ── footer ── */}
      <footer className="hp-footer">
        <div className="hp-footer__inner">
          <div className="hp-footer__brand">
            <span className="hp-nav__brand-icon">⬡</span>
            Look4Partner
          </div>
          <div className="hp-footer__links">
            <Link to="/matches">Find Matches</Link>
            <Link to="/contact-us">Contact Us</Link>
            <Link to="/signin">Sign In</Link>
            <Link to="/signup">Sign Up</Link>
          </div>
          <p className="hp-footer__copy">© {new Date().getFullYear()} Look4Partner. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;